import { Box, Button, Typography } from '@mui/material';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import styles from './User.module.scss';
import { memo, useEffect, useState } from 'react';
import { cast, handleApiError, notify } from '~/utils/helper';
import { AddNewIcon } from '~/assets/icons';
import dayjs from 'dayjs';
import BaseRow from '~/components/common/base-row/BaseRow';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { IMasterDataItem } from '~/utils/interface/master';
import { ActiveEnum } from '~/utils/enum';
import roleApi from '~/apis/role';
import UserList from './component/UserList';
import { handleGetUserList } from '~/thunks/user/userThunk';
import { userActions } from '~/thunks/user/userSlice';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT, PAGE_INDEX } from '~/utils/constants/common';
import EditUserDialog from './component/EditUserDialog';
import { IUser } from '~/utils/interface/user';
import ConfirmDialog from '~/components/common/confirm-dialog/ConfirmDialog';
import userApi from '~/apis/user';
import ResetPasswordUserDialog from './component/ResetPasswordUserDialog';
import BaseDatePicker from '~/components/common/base-date-picker/BaseDatePicker';

const cx = classNames.bind(styles);

const User = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('admin/user');

  const { filter, needRefreshData } = useAppSelector((state) => state.user);
  const { selectedUser, userProfile } = useAppSelector((state) => state.auth);

  const currentUser = Object.values(selectedUser).length === 0 ? userProfile : selectedUser;

  const [editData, setEditData] = useState<IUser>();
  const [roleOptions, setRoleOptions] = useState<IMasterDataItem[]>([]);

  const [isOpenEditUserDialog, setIsOpenEditUserDialog] = useState<boolean>(false);
  const [openEditUserMode, setOpenEditUserMode] = useState<string>(DIALOG_MODE_CREATE);

  const [isOpenActiveUserDialog, setIsOpenActiveUserDialog] = useState<boolean>(false);
  const [isOpenInactiveUserDialog, setIsOpenInactiveUserDialog] = useState<boolean>(false);

  const [isOpenDeleteUserDialog, setIsOpenDeleteUserDialog] = useState<boolean>(false);
  const [isOpenResetPasswordUserDialog, setIsOpenResetPasswordUserDialog] = useState<boolean>(false);

  const statusOptions = [
    {
      id: ActiveEnum.BLOCKED,
      name: t('status.' + ActiveEnum.BLOCKED),
    },
    {
      id: ActiveEnum.OPEN,
      name: t('status.' + ActiveEnum.OPEN),
    },
  ];

  useEffect(() => {
    if (!currentUser?.username) return;

    dispatch(handleGetUserList({ ...filter, username: currentUser?.username }))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
  }, [dispatch, filter, needRefreshData, currentUser?.username]);

  useEffect(() => {
    (async () => {
      try {
        const response = await roleApi.getList({});
        setRoleOptions(
          response.data.result?.map((item: any) => {
            return {
              id: item.id,
              name: item.rolename,
              data0: item.rolename,
              data1: item.usercreate,
              data2: item.powergroupid,
            };
          })
        );
      } catch (error) {
        setRoleOptions([]);
        handleApiError(error);
      }
    })();
  }, []);

  // Reset when change login
  useEffect(() => {
    return () => {
      dispatch(userActions.resetFilter());
    };
  }, [dispatch]);

  const handleClickAddNew = () => {
    setIsOpenEditUserDialog(true);
    setOpenEditUserMode(DIALOG_MODE_CREATE);
  };

  const handleOpenEdit = async (user: IUser) => {
    try {
      if (user) setOpenEditUserMode(DIALOG_MODE_EDIT);
      setEditData(user);
      setIsOpenEditUserDialog(true);
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleSetOpenUserEditDialog = (open: boolean, needRefreshData?: boolean) => {
    setIsOpenEditUserDialog(open);

    if (needRefreshData) dispatch(userActions.changeNeedRefreshData(true));
  };

  const handleClearFilter = () => {
    dispatch(userActions.resetFilter());
  };

  const handleOpenActive = async (user: IUser) => {
    setEditData(user);
    if (user.active === ActiveEnum.OPEN) setIsOpenInactiveUserDialog(true);
    else setIsOpenActiveUserDialog(true);
  };

  const hanldeConfirmActiveUser = async () => {
    try {
      if (!editData) return;

      const active = Number(editData.active);
      const username: string = cast(editData.username);
      const newActive = active === ActiveEnum.OPEN ? ActiveEnum.BLOCKED : ActiveEnum.OPEN;
      await userApi.active(username, { active: newActive });

      notify(
        'success',
        newActive === ActiveEnum.OPEN
          ? t('message.success.active', { username: cast(username) })
          : t('message.success.inactive', { username: cast(username) })
      );

      setIsOpenActiveUserDialog(false);
      setIsOpenInactiveUserDialog(false);
      dispatch(userActions.changeNeedRefreshData(true));
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleOpenDelete = async (user: IUser) => {
    setEditData(user);
    setIsOpenDeleteUserDialog(true);
  };

  const handleResetPassword = async (user: IUser) => {
    setEditData(user);
    setIsOpenResetPasswordUserDialog(true);
  };

  const handleDeleteConfirmUser = async () => {
    try {
      if (!editData) return;

      const username: string = cast(editData?.username);

      await userApi.delete(username);

      dispatch(userActions.changeNeedRefreshData(true));
      setIsOpenDeleteUserDialog(false);
      notify('success', t('message.success.delete', { username: username }));
    } catch (e) {
      handleApiError(e);
    }
  };

  return (
    <Box className={cx('user-screen')}>
      <Box className='flex justify-between items-center bg-[#123B6D] h-20 px-6'>
        <Typography className='text-white font-semibold text-xl'>{t('title')}</Typography>
        <Button
          className='bg-white rounded-xl text-[#123B6D] h-12 hover:bg-white hover:opacity-80 text-base font-bold px-5'
          startIcon={<AddNewIcon />}
          onClick={handleClickAddNew}
        >
          {t('create')}
        </Button>
      </Box>
      <BaseRow className='gap-4 px-6 pt-6 mb-7 2xl:!items-end max-[1600px]:!flex-col'>
        <Box className='w-full'>
          <Typography className='text-[#161A1F] font-medium text-sm mb-2'>{t('role')}</Typography>
          <BaseSelect
            options={roleOptions}
            value={filter.roleid ?? ''}
            placeholder={t('placeholder.roleid')}
            name='roleid'
            isDisplayAllOption
            isSearchEnable
            onChange={(e) => {
              const value = e.target.value;
              dispatch(
                userActions.setFilter({ roleid: value === '' ? undefined : Number(value), pageIndex: PAGE_INDEX })
              );
            }}
          />
        </Box>
        <Box className='w-full'>
          <Typography className='text-[#161A1F] font-medium text-sm mb-2'>{t('active')}</Typography>
          <BaseSelect
            options={statusOptions}
            value={filter.active ?? ''}
            placeholder={t('placeholder.active')}
            name='active'
            isSearchEnable
            isDisplayAllOption
            onChange={(e) => {
              const value = e.target.value;
              dispatch(
                userActions.setFilter({ active: value === '' ? undefined : Number(value), pageIndex: PAGE_INDEX })
              );
            }}
          />
        </Box>
        <Box className='w-full'>
          <Typography className='text-[#161A1F] font-medium text-sm mb-2'>{t('time_create_date')}</Typography>
          <Box className='flex justify-between items-center gap-3 max-[768px]:!flex-col'>
            <Box className='w-full'>
              <BaseDatePicker
                portalId='datePicker-portalId'
                value={filter.startDate}
                dateFormat='YYYY-MM-DD'
                onChange={(date) =>
                  dispatch(
                    userActions.setFilter({
                      startDate: dayjs(date).startOf('day').format('YYYY-MM-DD'),
                      pageIndex: PAGE_INDEX,
                    })
                  )
                }
                placeholder={t('startDate', { ns: 'admin/common' })}
                calendarProps={filter.endDate ? { maxDate: dayjs(filter.endDate).toDate() } : {}}
                className='w-full'
              />
            </Box>
            <Box className='w-4 h-px bg-black hidden lg:block'></Box>
            <Box className='w-full'>
              <BaseDatePicker
                portalId='datePicker-portalId'
                value={filter.endDate}
                dateFormat='YYYY-MM-DD'
                onChange={(date) =>
                  dispatch(
                    userActions.setFilter({
                      endDate: dayjs(date).endOf('day').format('YYYY-MM-DD'),
                      pageIndex: PAGE_INDEX,
                    })
                  )
                }
                placeholder={t('endDate', { ns: 'admin/common' })}
                calendarProps={filter.startDate ? { minDate: dayjs(filter.startDate).toDate() } : {}}
                className='w-full'
              />
            </Box>
            <Box className='flex-1'>
              <Button
                className='border border-solid border-[#123B6D] bg-[#DCE8F5] rounded-xl text-base font-bold text-[#123B6D] w-[132px] hover:bg-[#0E2F58] hover:opacity-80 hover:text-white'
                onClick={handleClearFilter}
              >
                {t('clear', { ns: 'admin/common' })}
              </Button>
            </Box>
          </Box>
        </Box>
      </BaseRow>

      <Box className='px-6'>
        <UserList
          onOpenEdit={handleOpenEdit}
          onOpenActive={handleOpenActive}
          onOpenDelete={handleOpenDelete}
          onResetPassword={handleResetPassword}
        />
      </Box>

      {isOpenEditUserDialog && (
        <EditUserDialog
          data={editData}
          currentUser={currentUser}
          open={isOpenEditUserDialog}
          mode={openEditUserMode}
          setOpen={handleSetOpenUserEditDialog}
        />
      )}

      {isOpenActiveUserDialog && (
        <ConfirmDialog
          open={isOpenActiveUserDialog}
          title={t('message.title.active')}
          description={t('message.description.active')}
          confirm={t('button.active')}
          onConfirm={hanldeConfirmActiveUser}
          onCancel={() => setIsOpenActiveUserDialog(false)}
          btnConfirmClassName={isOpenActiveUserDialog ? 'bg-[#123B6D]' : ''}
        />
      )}

      {isOpenInactiveUserDialog && (
        <ConfirmDialog
          open={isOpenInactiveUserDialog}
          title={t('message.title.inactive')}
          description={t('message.description.inactive')}
          confirm={t('button.inactive')}
          onConfirm={hanldeConfirmActiveUser}
          onCancel={() => setIsOpenInactiveUserDialog(false)}
          btnConfirmClassName={isOpenActiveUserDialog ? 'bg-[#123B6D]' : ''}
        />
      )}

      {isOpenDeleteUserDialog && (
        <ConfirmDialog
          open={isOpenDeleteUserDialog}
          title={t('message.title.delete')}
          description={t('message.description.delete', { username: editData?.username })}
          confirm={t('button.delete')}
          onConfirm={handleDeleteConfirmUser}
          onCancel={() => setIsOpenDeleteUserDialog(false)}
        />
      )}

      {isOpenResetPasswordUserDialog && (
        <ResetPasswordUserDialog
          open={isOpenResetPasswordUserDialog}
          data={editData}
          setOpen={(open) => setIsOpenResetPasswordUserDialog(open)}
        />
      )}
    </Box>
  );
};

export default memo(User);
